import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ShoppingBag, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useUserStore } from '../store/userStore';
import { ordersApi, type UserOrderListItem } from '../services/ordersApi';
import { myOrdersQueryOptions, myOrderStatusQueryKey } from '../queries/myOrders';
import { isTerminalOrderStatus, orderStatusLabel } from '../utils/orderStatusLabels';
import { OrderDetailSheet } from '../components/ActiveOrderBanner';

const ORDERS_HISTORY_STAGGER_KEY = 'theories_stagger_orders_history';

function shouldPlayOrdersHistoryStagger(): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(ORDERS_HISTORY_STAGGER_KEY) !== '1';
  } catch {
    return true;
  }
}

function itemCount(items: unknown): number {
  return Array.isArray(items) ? items.length : 0;
}

function statusStyle(code: string | undefined) {
  if (!code) return { bg: 'rgba(0,0,0,0.06)', color: '#888' };
  if (isTerminalOrderStatus(code)) {
    return code === 'Cancelled'
      ? { bg: '#ef444415', color: '#ef4444' }
      : { bg: '#10b98115', color: '#10b981' };
  }
  return { bg: '#f59e0b15', color: '#f59e0b' };
}

export default function OrdersHistoryScreen() {
  const navigate = useNavigate();
  const token = useUserStore((s) => s.token);
  const [detailOrder, setDetailOrder] = useState<UserOrderListItem | null>(null);

  const { data: orders = [], isLoading, isError } = useQuery(myOrdersQueryOptions(token));

  const rows: UserOrderListItem[] = orders
    .filter(o => !(o.paymentMethod === 'online' && o.paymentStatus === 'pending' && !o.iikoOrderId))
    .slice(0, 25);
  const playOrdersStagger = shouldPlayOrdersHistoryStagger();

  useEffect(() => {
    if (!playOrdersStagger || rows.length === 0) return;
    const ms = Math.min(rows.length * 40 + 400, 2500);
    const t = window.setTimeout(() => {
      try {
        sessionStorage.setItem(ORDERS_HISTORY_STAGGER_KEY, '1');
      } catch {
        /* ignore */
      }
    }, ms);
    return () => clearTimeout(t);
  }, [playOrdersStagger, rows.length]);
  const statusQueries = useQueries({
    queries: rows.map((o) => ({
      queryKey: myOrderStatusQueryKey(token, o.id),
      queryFn: () => ordersApi.getMyOrderStatus(token!, o.id),
      enabled: !!token,
      staleTime: 30_000,
    })),
  });

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f4f4f4' }}>
      <div className="max-w-md mx-auto w-full px-4 pt-6 pb-10">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white shadow-sm border border-black/5 active:scale-90 transition-all"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">История заказов</h1>
        </div>

        {!token && (
          <p className="text-center text-black/40 py-16">Войдите, чтобы видеть заказы</p>
        )}

        {token && isLoading && (
          <p className="text-center text-black/40 py-16">Загрузка…</p>
        )}

        {token && isError && (
          <p className="text-center text-black/40 py-16">Не удалось загрузить заказы</p>
        )}

        {token && !isLoading && !isError && (
          <div className="space-y-4">
            {rows.map((order, i) => {
              const st = statusQueries[i]?.data?.status;
              const label = st ? orderStatusLabel(st) : '…';
              const { bg, color } = statusStyle(st);
              const total = order.totalAmount != null ? Math.round(order.totalAmount) : null;
              const when = new Date(order.createdAt).toLocaleString('ru-RU', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              });
              const shortId = `№ ${String(order.orderNumber).padStart(6, '0')}`;

              const card = (
                <button
                  type="button"
                  className="w-full text-left bg-white rounded-3xl p-5 shadow-sm border border-black/5 active:scale-[0.99] transition-transform"
                  onClick={() => setDetailOrder(order)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-black/5 flex items-center justify-center shrink-0">
                        <ShoppingBag size={18} className="text-black/40" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-sm text-[#1a1a1a] truncate">{shortId}</h3>
                        <div className="flex items-center gap-1 text-[10px] text-black/30 font-medium">
                          <Clock size={12} />
                          {when}
                        </div>
                      </div>
                    </div>
                    <div
                      className="text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 ml-2"
                      style={{ background: bg, color }}
                    >
                      {label}
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs text-black/40 mb-1">Состав</p>
                    <p className="text-sm font-medium text-black/70">
                      {itemCount(order.items)} {itemCount(order.items) === 1 ? 'позиция' : 'позиций'}
                    </p>
                  </div>

                  <div className="h-px bg-black/5 mb-4" />

                  {total != null && (
                    <div className="text-lg font-bold text-[#1a1a1a]">{total.toLocaleString('ru')} ₽</div>
                  )}
                </button>
              );

              return playOrdersStagger ? (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  {card}
                </motion.div>
              ) : (
                <React.Fragment key={order.id}>{card}</React.Fragment>
              );
            })}

            {rows.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 rounded-full bg-black/5 flex items-center justify-center mx-auto mb-4 text-3xl">
                  📦
                </div>
                <p className="text-black/40 font-medium">У вас пока нет заказов</p>
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {detailOrder && token ? (
          <OrderDetailSheet
            key={detailOrder.id}
            order={detailOrder}
            token={token}
            onClose={() => setDetailOrder(null)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
